import { ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnInit, Output, EventEmitter, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CardStatus, CardConfiguration, Icon, Permissions, AccessLevels } from '@bciti/types';
import { TranslateService } from '@ngx-translate/core';
import { Subject, combineLatest, forkJoin, of } from 'rxjs';
import { map, switchMap, takeUntil, take, catchError, filter } from 'rxjs/operators';
import { DocumentViewerComponent } from 'src/app/global/components/common/document-viewer/modal-image.component';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { UserService } from 'src/app/user/service/user.abstract.service';
import { SearchUsersAction } from 'src/app/user/store/user.actions';
import { CardActions, CardVerificationResponse, UserCard, UserRecordTransformedUser } from 'src/app/user/user.model';
import { formatDateOnly, parseDateForDisplay } from 'src/app/global/utils/date/date.utils';
import { LanguageConfiguration } from 'src/app/global/global.model';
import { languageSelector, organisationConfigurationSelector, organisationTagSelector } from 'src/app/global/store/global.selectors';
import { ResidenceService } from 'src/app/residence/service/residence.abstract.service';
import { ProfileAddress, getResidenceDisplayState, needsReview as residenceNeedsReview } from 'src/app/residence/residence.model';
import { GeoidAddressModalComponent } from 'src/app/global/components/forms/geoid-address/modal/geoid-address-modal.component';
import { ConfirmDialogComponent } from 'src/app/global/components/dialogs/confirm-dialog/confirm-dialog.component';
import { ErrorSeverity, InformationalDialogComponent } from '../../../global/components/dialogs/informational-dialog/informational-dialog.component';
import { UserRequestDenyDialogComponent } from '../../dialogs/user-request-deny-dialog/user-request-deny-dialog.component';
import { UserFamilyAndCardDialogComponent } from '../../dialogs/user-family-and-card-dialog/user-family-and-card-dialog.component';
import { userScopesSelector } from 'src/app/user/store/user.selectors';
import { UserUtilService } from '../../util/user-util.service';

export interface UserCardModified extends UserCard {
    proofOfIdentity: string[];
}

export interface UserCardRequestFormData {
    user: UserRecordTransformedUser;
    card: UserCardModified;
    action: CardActions;
}

@Component({
    selector: 'app-user-card-request-detail-form',
    templateUrl: './user-card-request-detail-form.component.html',
    styleUrls: ['./user-card-request-detail-form.component.scss'],
})
export class UserCardRequestDetailFormComponent implements OnInit {
    public destroy$ = new Subject();

    Icon = Icon;

    CardStatus = CardStatus;

    CardActions = CardActions;

    public requestCardForm: FormGroup;

    public backgroundCheck: any;

    public loading = false;

    public discrepancies: any;

    public verification: CardVerificationResponse;
    public selectedExternalId: string | null = null;
    public selectedCardNumber: string | null = null;
    public verificationLoading = true;
    public isCardConnected = false;

    // Map to store detected file types for each URL
    public fileTypeMap: Map<string, 'image' | 'pdf' | 'unknown'> = new Map();
    public fileTypesLoading = true;

    cardConfig: CardConfiguration;

    currentLang: string;

    orgLang: LanguageConfiguration;

    isSubmitted = false;

    isApprovalPressed = false;

    approvalGuidelines: string;
    showApprovalGuide = false;

    // Edit mode properties
    editMode = false;
    editForm: FormGroup;
    editSaving = false;
    canWriteCard = false;
    newAttachmentFiles: File[] = [];
    removedAttachmentUrls: string[] = [];
    maxBirthDate = new Date();
    orgTag: string;

    get sexOptions() {
        const options = [
            { id: 1, value: 'female', label: `${this.translateService.instant('pages.user-cards.create.steps.information.female')}` },
            { id: 2, value: 'male', label: `${this.translateService.instant('pages.user-cards.create.steps.information.male')}` },
            { id: 3, value: 'other', label: `${this.translateService.instant('pages.user-cards.create.steps.information.other')}` },
        ];

        return this.orgTag === 'BROSSARD' ? options.filter((option) => option.value !== 'other') : options;
    }

    @ViewChild('pictureUploader') pictureUploader: any;

    // Address list for multi-residence
    public userAddresses: ProfileAddress[] = [];
    public selectedAddressIndex = 0;

    @Input() data: UserCardRequestFormData;

    @Output() approvalPressed = new EventEmitter<{ userId: string; cardId: string }>();

    get cardLabel(): string {
        return this.cardConfig?.identity?.label[this.currentLang];
    }

    get cardIcon(): string {
        return this.cardConfig?.identity?.icon || Icon.MY_CARD;
    }

    get userProfileLink(): string {
        return this.data.user?.id ? `/user-update/${this.data.user.id}` : '';
    }

    get isApproveDisabled(): boolean {
        return this.verification?.verificationType === 'DUPLICATE_BLOCKED' || (this.verification?.verificationType === 'CARD_EXISTS_INFO_MISMATCH' && !this.selectedExternalId);
    }

    get isStatusRequired(): boolean {
        return this.subTypes?.length > 0 && !this.selectedAddressSubType && (this.requestCardForm?.invalid ?? false);
    }

    constructor(
        private formBuilder: FormBuilder,
        private translateService: TranslateService,
        private service: UserService,
        private messageService: MessageService,
        private dialog: MatDialog,
        private dialogRef: MatDialogRef<UserFamilyAndCardDialogComponent>,
        private store: Store<State>,
        private cdr: ChangeDetectorRef,
        private elementRef: ElementRef,
        private residenceService: ResidenceService,
        private userUtilService: UserUtilService,
    ) {}

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (this.showApprovalGuide && !this.elementRef.nativeElement.querySelector('.files-header-row')?.contains(event.target)) {
            this.showApprovalGuide = false;
        }
    }

    approveLoading = false;

    denyLoading = false;

    public subTypes = [];

    ngOnInit(): void {
        this.loading = true;

        this.store
            .select(userScopesSelector)
            .pipe(
                filter((scopes) => scopes !== undefined),
                take(1),
            )
            .subscribe((scopes) => {
                if (scopes && this.data?.card?.cardType) {
                    this.canWriteCard = scopes.some(
                        (scope) =>
                            scope.permission === Permissions.CARD &&
                            scope.resource === 'card_type' &&
                            (scope.resourceId === '*' || scope.resourceId === this.data.card.cardType) &&
                            scope.accessLevel === AccessLevels.WRITE,
                    );
                }
            });

        combineLatest([
            this.store.select(languageSelector),
            this.store.select(organisationConfigurationSelector),
            this.store.select(organisationTagSelector),
            this.service.cardAddressExists(this.data.card._id),
            this.service.cardDiscrepancies(this.data.card._id, this.data.user.id),
        ])
            .pipe(
                takeUntil(this.destroy$),
                map(([lang, orgConfig, orgTag, cardAddressExists, discrepancies]) => {
                    this.orgTag = orgTag;
                    this.backgroundCheck = cardAddressExists?.backgroundCheck;
                    this.discrepancies = discrepancies;
                    this.currentLang = lang;
                    this.orgLang = orgConfig.lang;
                    this.cardConfig = orgConfig?.support?.cards?.find((c) => c.type === this.data.card.cardType);
                    this.approvalGuidelines = (this.cardConfig as any)?.approvalGuidelines?.[lang] || '';
                    if (this.cardConfig?.availableSubTypes?.length > 0) {
                        this.requestCardForm = this.formBuilder.group({ subType: new FormControl('', [Validators.required]) });
                        const subTypes = [];
                        this.cardConfig?.availableSubTypes.forEach((availableSubType) => {
                            const subType = orgConfig?.subTypes?.find((s) => s.tag === availableSubType);
                            subTypes.push({ label: subType?.name?.[lang], value: subType.tag });
                        });
                        this.subTypes = subTypes;
                    }
                    this.loading = false;
                    // Detect file types for all attachments
                    this.detectAllFileTypes();
                    // Load user addresses with residence enrichment
                    this.loadUserAddresses();
                    // Fetch verification if card is connected
                    this.isCardConnected = this.cardConfig?.isConnected || (orgTag === 'STECATHERINE' && this.data.card.cardType === 'LIBRARY');
                    this.fetchVerification();
                }),
            )
            .subscribe();
    }

    private fetchVerification(): void {
        if (this.isCardConnected) {
            this.verificationLoading = true;
            this.selectedExternalId = null;
            this.selectedCardNumber = null;
            this.service
                .cardVerification(this.data.card._id, this.data.user.id)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (verification) => {
                        this.verification = verification;
                        this.verificationLoading = false;

                        if (verification.verificationType === 'PARTIAL_MATCH') {
                            this.selectedExternalId = null;
                            this.selectedCardNumber = null;
                        } else if (verification.verificationType === 'EXACT_MATCH' && verification.matches.length > 0) {
                            this.selectedExternalId = verification.matches[0].externalId;
                            this.selectedCardNumber = verification.matches[0].code || verification.matches[0].number;
                        }
                        this.cdr.detectChanges();
                    },
                    error: () => {
                        this.verificationLoading = false;
                        this.cdr.detectChanges();
                    },
                });
        } else {
            this.verificationLoading = false;
        }
    }

    private loadUserAddresses(): void {
        const userId = this.data.user.id;

        // Fetch full user record to get addresses, then enrich with residences
        combineLatest([this.service.getUser(userId), this.residenceService.getCitizenResidences(userId)])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([userRecord, residences]) => {
                const addresses = userRecord?.addresses || [];
                this.userAddresses = this.mergeAddresses(addresses, residences);
                this.preselectCardAddress();
                this.cdr.detectChanges();
            });
    }

    private mergeAddresses(userAddresses: any[], residences: any[]): ProfileAddress[] {
        const matched = new Set<string>();
        return userAddresses.map((addr) => {
            const addrStreet = (addr.streetName || addr.street || '').replace(/\s+/g, '').toLowerCase();
            const addrPostCode = (addr.postCode || '').replace(/\s+/g, '').toLowerCase();
            const addrNumber = String(addr.number || '');
            const matchingResidences = residences.filter(
                (r) =>
                    !matched.has(r._id) &&
                    (r.address.streetName || r.address.street || '').replace(/\s+/g, '').toLowerCase() === addrStreet &&
                    String(r.address.number || '') === addrNumber &&
                    (r.address.postCode || '').replace(/\s+/g, '').toLowerCase() === addrPostCode,
            );
            matchingResidences.forEach((r) => matched.add(r._id));
            const orgLinks = matchingResidences.map((r) => ({
                tag: r.organisation,
                name: r.organisation,
                isPrimary: r.isPrimary,
                verificationStatus: r.verification.status,
                residenceId: r._id,
                displayState: getResidenceDisplayState(r),
                needsReview: residenceNeedsReview(r),
                expirationDate: r.expirationDate,
                subType: r.subType,
            }));
            return {
                address: addr,
                displayName: matchingResidences[0]?.name ?? addr.name ?? `${addr.streetName}, ${addr.city}`,
                orgLinks,
                isCasual: orgLinks.length === 0,
                isVerified: orgLinks.some((link) => link.verificationStatus === 'APPROVED'),
            };
        });
    }

    private preselectCardAddress(): void {
        const cardAddr = this.data.card.renewal?.address || this.data.card.address;
        if (!cardAddr || this.userAddresses.length === 0) return;
        const cardStreet = (cardAddr.streetName || cardAddr.street || '').replace(/\s+/g, '').toLowerCase();
        const cardNumber = String(cardAddr.number || '');
        const cardPostCode = (cardAddr.postCode || '').replace(/\s+/g, '').toLowerCase();
        const idx = this.userAddresses.findIndex((pa) => {
            const a = pa.address;
            return (
                (a.streetName || a.street || '').replace(/\s+/g, '').toLowerCase() === cardStreet &&
                String(a.number || '') === cardNumber &&
                (a.postCode || '').replace(/\s+/g, '').toLowerCase() === cardPostCode
            );
        });
        if (idx >= 0) this.selectedAddressIndex = idx;
    }

    public selectAddress(index: number): void {
        this.selectedAddressIndex = index;
        const addr = this.userAddresses[index]?.address;
        if (addr) {
            // Update card data with selected address
            if (this.editMode && this.editForm) {
                this.editForm.get('address').setValue(addr);
            }
        }
    }

    public addNewAddress(): void {
        this.dialog
            .open(GeoidAddressModalComponent, { data: { showSubTypeSelector: true } })
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                if (result) {
                    const address = result.address;
                    const subType = result.subType || 'RESIDENT';
                    // Create residence via admin endpoint
                    this.store
                        .select(organisationTagSelector)
                        .pipe(
                            switchMap((orgTag) =>
                                this.residenceService.createResidence({
                                    userId: this.data.user.id,
                                    address,
                                    organisation: orgTag,
                                    subType,
                                }),
                            ),
                            takeUntil(this.destroy$),
                        )
                        .subscribe({
                            next: () => {
                                this.loadUserAddresses();
                                this.messageService.success(this.translateService.instant('pages.profile.addresses.addSuccess'));
                            },
                            error: () => {
                                this.messageService.error(this.translateService.instant('pages.residences.toast.error'));
                            },
                        });
                }
            });
    }

    get selectedAddress(): any {
        return this.userAddresses[this.selectedAddressIndex]?.address;
    }

    public deleteAddress(pa: ProfileAddress): void {
        const addressId = (pa.address as any)._id || (pa.address as any).id;
        if (!addressId) return;

        const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
            width: '500px',
            panelClass: 'bciti-confirmation-dialog',
            data: {
                title: this.translateService.instant('pages.profile.addresses.delete'),
                body: this.translateService.instant('pages.profile.addresses.deleteConfirm', { name: pa.displayName }),
                buttons: {
                    confirm: {
                        text: this.translateService.instant('pages.profile.addresses.delete'),
                        class: 'bciti-button bciti-button--red',
                    },
                    cancel: {
                        text: this.translateService.instant('pages.residences.reviewDialog.cancelButton'),
                        class: 'bciti-button bciti-button--blue-inversed',
                    },
                },
            },
        });

        confirmDialog.componentInstance.onSubmit.subscribe((confirmed) => {
            confirmDialog.componentInstance.dialogRef.close();
            if (confirmed) {
                this.service
                    .deleteUserAddress(this.data.user.id, addressId)
                    .pipe(
                        take(1),
                        catchError((error) => {
                            this.messageService.error(this.userUtilService.resolveApiErrorMessage(error, 'pages.residences.toast.error'));
                            return of(null);
                        }),
                    )
                    .subscribe((result) => {
                        if (result !== null) {
                            this.messageService.success(this.translateService.instant('pages.profile.addresses.deleteSuccess'));
                            this.loadUserAddresses();
                        }
                    });
            }
        });
    }

    get selectedAddressSubType(): string | null {
        return this.userAddresses[this.selectedAddressIndex]?.orgLinks?.[0]?.subType || null;
    }

    closeDialog(v?: boolean | { success: boolean; openFamilyDialogForUser?: UserRecordTransformedUser }): void {
        if (this.dialogRef?.close) {
            this.dialogRef.close(v);
        }
    }

    /**
     * Close dialog with user info if they have pending family requests
     * This enables chaining to family dialog after card approval
     */
    private closeDialogWithChaining(): void {
        // Check if user has pending family requests by examining the family array directly
        // A pending request is one where familyApproved is true but cityApproved is false
        // Note: userFamily.family is an array of FamilyLink objects
        const familyLinks = this.data.user?.userFamily?.family as unknown as Array<{
            familyApproved: boolean;
            cityApproved: boolean;
        }>;
        const hasPendingFamilyRequest = familyLinks?.some((link) => link.familyApproved && !link.cityApproved);

        if (hasPendingFamilyRequest) {
            this.closeDialog({ success: true, openFamilyDialogForUser: this.data.user });
        } else {
            this.closeDialog(true);
        }
    }

    handleApprove(): void {
        this.isSubmitted = true;
        this.isApprovalPressed = true;

        this.approvalPressed.emit({
            userId: this.data.user.id,
            cardId: this.data.card._id,
        });

        if (!this.requestCardForm || this.requestCardForm.valid || !!this.selectedAddressSubType) {
            if (this.data.action === CardActions.SUBSCRIBE) {
                this.approveCardRequest();
            }
            if (this.data.action === CardActions.RENEW) {
                this.approveCardRenewal();
            }
        } else {
            this.messageService.error(this.translateService.instant('pages.userRecords.card.requestDetailsDialog.message.approve.errorStatus'));
        }
    }

    handleDeny(): void {
        this.closeDialog(true);
        let langLabel = this.translateService.instant(`common.langs.${this.orgLang.default}`);
        if (this.data.user.preferences?.language && this.orgLang.available.includes(this.data.user.preferences?.language)) {
            langLabel = this.translateService.instant(`common.langs.${this.data.user.preferences.language}`);
        }
        const locale = this.currentLang || this.orgLang.default;
        const denialReasons = (this.cardConfig as any)?.denialReasons?.[locale] || [];
        const dialog = this.dialog.open(UserRequestDenyDialogComponent, {
            autoFocus: false,
            width: '680px',
            panelClass: '',
            data: {
                title: this.translateService.instant('pages.userRecords.card.denyCardDialog.header'),
                body: {
                    label: this.translateService.instant('pages.userRecords.card.denyCardDialog.label', { lang: langLabel }),
                    placeholder: this.translateService.instant('pages.userRecords.card.denyCardDialog.placeholder'),
                    error: this.translateService.instant('pages.userRecords.card.denyCardDialog.form.errors.reason'),
                    button: this.translateService.instant('pages.userRecords.card.denyCardDialog.button.submit'),
                    presetTab: this.translateService.instant('pages.userRecords.card.denyCardDialog.presetTab'),
                    customTab: this.translateService.instant('pages.userRecords.card.denyCardDialog.customTab'),
                },
                denialReasons,
            },
        });

        dialog.componentInstance.onSubmit.subscribe((reason) => {
            dialog.componentInstance.dialogRef.close();
            if (this.data.action === CardActions.SUBSCRIBE) {
                this.denyCardRequest(reason);
            }
            if (this.data.action === CardActions.RENEW) {
                this.denyCardRenewal(reason);
            }
        });
    }

    approveCardRenewal(): void {
        this.approveLoading = true;
        this.service
            .approveUserCardRenewal(this.data.user.id, this.data.card._id, this.selectedAddressSubType || this.requestCardForm?.value?.subType?.value)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (_response) => {
                    this.approveLoading = false;
                    this.messageService.success(
                        this.translateService.instant('pages.userRecords.card.requestDetailsDialog.message.approveRenewal.success', {
                            firstName: this.data.user.firstName,
                            lastName: this.data.user.lastName,
                        }),
                        undefined,
                        { enableHtml: true },
                    );
                    this.closeDialogWithChaining();
                },
                (_error) => {
                    this.approveLoading = false;
                    this.messageService.error(this.translateService.instant('pages.userRecords.card.requestDetailsDialog.message.approveRenewal.error'));
                },
            );
    }

    denyCardRenewal(reason: string): void {
        this.denyLoading = true;
        this.service
            .denyUserCardRenewal(this.data.user.id, this.data.card._id, reason)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (_response) => {
                    this.denyLoading = false;
                    this.messageService.success(
                        this.translateService.instant('pages.userRecords.card.requestDetailsDialog.message.denyRenewal.success', {
                            firstName: this.data.user.firstName,
                            lastName: this.data.user.lastName,
                        }),
                        undefined,
                        { enableHtml: true },
                    );
                    this.closeDialog(true);
                },
                (_error) => {
                    this.approveLoading = false;
                    this.messageService.error(this.translateService.instant('pages.userRecords.card.requestDetailsDialog.message.denyRenewal.error'));
                },
            );
    }

    approveCardRequest(): void {
        this.approveLoading = true;
        this.service
            .approveUserCard(this.data.user.id, this.data.card._id, this.selectedAddressSubType || this.requestCardForm?.value?.subType?.value, this.selectedExternalId, this.selectedCardNumber)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (_response) => {
                    this.approveLoading = false;
                    this.messageService.success(
                        this.translateService.instant('pages.userRecords.card.requestDetailsDialog.message.approve.success', {
                            firstName: this.data.user.firstName,
                            lastName: this.data.user.lastName,
                        }),
                        undefined,
                        { enableHtml: true },
                    );
                    this.closeDialogWithChaining();
                },
                (error) => {
                    this.approveLoading = false;
                    if (error.status === 409) {
                        this.dialog
                            .open(InformationalDialogComponent, {
                                panelClass: 'bciti-error-dialog',
                                data: {
                                    title: this.translateService.instant('pages.user-cards.create.errors.createConflictSubject'),
                                    body: this.translateService.instant('pages.user-cards.create.errors.createConflictDescription', {
                                        cardEmail: error.error?.messages[0]?.split(':')[1]?.trim() || '',
                                    }),
                                    errorSeverity: ErrorSeverity.Error,
                                },
                            })
                            .afterClosed()
                            .subscribe(() => {
                                this.dialogRef.close();
                            });
                    } else {
                        this.messageService.error(this.translateService.instant('pages.userRecords.card.requestDetailsDialog.message.approve.error'));
                    }
                },
            );
    }

    denyCardRequest(reason: string): void {
        this.denyLoading = true;
        this.service
            .denyUserCard(this.data.user.id, this.data.card._id, reason)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (_response) => {
                    this.denyLoading = false;
                    this.messageService.success(
                        this.translateService.instant('pages.userRecords.card.requestDetailsDialog.message.deny.success', {
                            firstName: this.data.user.firstName,
                            lastName: this.data.user.lastName,
                        }),
                        undefined,
                        { enableHtml: true },
                    );
                    this.closeDialog(true);
                },
                (_error) => {
                    this.denyLoading = false;
                    this.messageService.error(this.translateService.instant('pages.userRecords.card.requestDetailsDialog.message.deny.error'));
                },
            );
    }

    openPictureUpload(): void {
        if (this.pictureUploader) {
            this.pictureUploader.openDialog();
        }
    }

    enterEditMode(): void {
        const { card, user } = this.data;
        const { renewal } = card;

        this.editForm = new FormGroup({
            firstName: new FormControl(renewal?.firstName || card.firstName || '', [Validators.required, Validators.minLength(2)]),
            lastName: new FormControl(renewal?.lastName || card.lastName || '', [Validators.required, Validators.minLength(2)]),
            picture: new FormControl(renewal?.photo || card.photo || '', [Validators.required]),
            birthDate: new FormControl('', [Validators.required, this.validateDateIsInPast]),
            address: new FormControl(this.selectedAddress || renewal?.address || card.address || '', [Validators.required]),
            sex: new FormControl('', [Validators.required]),
        });

        // Set birthDate using parseDateForDisplay
        const birthDateSource = renewal?.birthDate || card.birthDate || user.birthDate;
        if (birthDateSource) {
            this.editForm.controls.birthDate.setValue(parseDateForDisplay(birthDateSource) || '');
        }

        // Set sex option
        const sexSource = renewal?.sex || card.sex || user.sex;
        if (sexSource) {
            this.editForm.controls.sex.setValue(this.sexOptions.find((option) => option.value === sexSource) || this.sexOptions[0]);
        }

        this.newAttachmentFiles = [];
        this.removedAttachmentUrls = [];
        this.newFilePreviewUrls.forEach((url) => URL.revokeObjectURL(url));
        this.newFilePreviewUrls.clear();
        this.editMode = true;
    }

    cancelEdit(): void {
        this.editMode = false;
        this.newAttachmentFiles = [];
        this.removedAttachmentUrls = [];
        this.newFilePreviewUrls.forEach((url) => URL.revokeObjectURL(url));
        this.newFilePreviewUrls.clear();
    }

    get activeAttachments(): string[] {
        const isRenewal = this.data.action === CardActions.RENEW && this.data.card?.renewal?.proofOfIdentity?.length > 0;
        const source = isRenewal ? this.data.card.renewal.proofOfIdentity : this.data.card?.proofOfIdentity || [];
        return source.filter((url) => !this.removedAttachmentUrls.includes(url));
    }

    removeExistingAttachment(url: string): void {
        this.removedAttachmentUrls.push(url);
    }

    onNewFilesChanged(files: File[]): void {
        this.newAttachmentFiles = files;
    }

    newFilePreviewUrls: Map<File, string> = new Map();

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files) {
            Array.from(input.files).forEach((file) => {
                this.newAttachmentFiles.push(file);
                const previewUrl = URL.createObjectURL(file);
                this.newFilePreviewUrls.set(file, previewUrl);
            });
        }
        input.value = '';
    }

    removeNewAttachment(index: number): void {
        const file = this.newAttachmentFiles[index];
        const previewUrl = this.newFilePreviewUrls.get(file);
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            this.newFilePreviewUrls.delete(file);
        }
        this.newAttachmentFiles.splice(index, 1);
    }

    isNewFileImage(file: File): boolean {
        return file.type.startsWith('image/');
    }

    getNewFilePreview(file: File): string {
        return this.newFilePreviewUrls.get(file) || '';
    }

    saveEdit(): void {
        if (!this.editForm.valid) {
            return;
        }
        this.editSaving = true;

        // Upload new attachment files
        const uploadObservables = this.newAttachmentFiles.length > 0 ? this.newAttachmentFiles.map((file) => this.service.postDocuments(file)) : [];

        const upload$ = uploadObservables.length > 0 ? forkJoin(uploadObservables) : of([] as string[][]);

        upload$
            .pipe(
                switchMap((uploadResults: string[][]) => {
                    // Each result is a string[] of URLs; take first from each
                    const newUrls = uploadResults.map((urls) => urls[0]);

                    // Merge: existing (minus removed) + newly uploaded
                    const mergedProofOfIdentity = [...this.activeAttachments, ...newUrls];

                    const payload: any = {
                        firstName: this.editForm.get('firstName').value,
                        lastName: this.editForm.get('lastName').value,
                        sex: this.editForm.get('sex').value?.value,
                        birthDate: formatDateOnly(this.editForm.get('birthDate').value),
                        address: this.editForm.get('address').value,
                        photo: this.editForm.get('picture').value,
                        proofOfIdentity: mergedProofOfIdentity,
                        cardTypes: [this.data.card.cardType],
                        subType: (this.data.card as any).subType || '',
                    };

                    return this.service.patchUpdateCard(payload, this.data.card._id);
                }),
                takeUntil(this.destroy$),
            )
            .subscribe(
                () => {
                    // Replace data.card with a new object so view mode reflects changes
                    // (the original object may be frozen/read-only from NgRx store)
                    const formValues = this.editForm.value;
                    const updatedFields = {
                        firstName: formValues.firstName,
                        lastName: formValues.lastName,
                        sex: formValues.sex?.value,
                        birthDate: formatDateOnly(formValues.birthDate),
                        address: formValues.address,
                        photo: formValues.picture,
                        proofOfIdentity: [...this.activeAttachments],
                    };

                    if (this.data.action === CardActions.RENEW && this.data.card.renewal) {
                        this.data = {
                            ...this.data,
                            card: {
                                ...this.data.card,
                                renewal: { ...this.data.card.renewal, ...updatedFields },
                            },
                        };
                    } else {
                        this.data = {
                            ...this.data,
                            card: { ...this.data.card, ...updatedFields },
                        };
                    }

                    this.editSaving = false;
                    this.editMode = false;
                    this.newAttachmentFiles = [];
                    this.removedAttachmentUrls = [];
                    this.detectAllFileTypes();
                    this.store.dispatch(SearchUsersAction());
                    this.fetchVerification();
                    this.cdr.detectChanges();
                    this.messageService.success(this.translateService.instant('pages.userRecords.card.requestDetailsDialog.edit.success'));
                },
                () => {
                    this.editSaving = false;
                    this.cdr.detectChanges();
                    this.messageService.error(this.translateService.instant('pages.userRecords.card.requestDetailsDialog.edit.error'));
                },
            );
    }

    validateDateIsInPast(c: FormControl): { [key: string]: { [key: string]: boolean } } {
        if (c.value) {
            const today = new Date().toISOString();
            return new Date(c.value).toISOString() < today ? null : { validateDateIsInPast: { valid: false } };
        }
        return null;
    }

    viewDocument(url: string): void {
        this.dialog.open(DocumentViewerComponent, {
            maxWidth: '100vw',
            maxHeight: '100vh',
            data: { src: url },
            panelClass: 'document-viewer-dialog',
        });
    }

    formatDate(date: string): string {
        if (!date) {
            return '';
        }
        // Extract date portion from ISO string to avoid timezone conversion issues
        let dateString = date;
        if (date.includes('T')) {
            dateString = date.split('T')[0];
        }
        // Parse date components and create Date in local timezone
        const [year, month, day] = dateString.split('-').map(Number);
        const tempDate = new Date(year, month - 1, day);
        const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Intl.DateTimeFormat(`${this.translateService.currentLang}-CA`, options).format(tempDate);
    }

    /**
     * Extract the file path from a URL, removing query parameters
     */
    private getUrlPath(url: string): string {
        try {
            const urlObj = new URL(url);
            return urlObj.pathname;
        } catch {
            const queryIndex = url.indexOf('?');
            return queryIndex > -1 ? url.substring(0, queryIndex) : url;
        }
    }

    /**
     * Check if URL has a known image extension
     */
    private hasImageExtension(url: string): boolean {
        const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.jfif', '.jpe', '.svg', '.tiff', '.tif', '.ico'];
        const path = this.getUrlPath(url);
        const extension = path.substring(path.lastIndexOf('.')).toLowerCase();
        return imageExtensions.includes(extension);
    }

    /**
     * Detect file types for all attachments
     */
    async detectAllFileTypes(): Promise<void> {
        const urls: string[] = [];

        // Collect all attachment URLs
        if (this.data.card?.proofOfIdentity?.length > 0) {
            urls.push(...this.data.card.proofOfIdentity);
        }
        if (this.data.card?.renewal?.proofOfIdentity?.length > 0 && this.data.action === CardActions.RENEW) {
            urls.push(...this.data.card.renewal.proofOfIdentity);
        }

        // Detect file type for each URL
        await Promise.all(urls.map((url) => this.detectFileType(url)));
        this.fileTypesLoading = false;
    }

    /**
     * Detect file type for a single URL using extension or content-type header
     */
    async detectFileType(url: string): Promise<void> {
        // First check by extension
        if (this.hasImageExtension(url)) {
            this.fileTypeMap.set(url, 'image');
            return;
        }

        const path = this.getUrlPath(url);
        const extension = path.substring(path.lastIndexOf('.')).toLowerCase();
        if (extension === '.pdf') {
            this.fileTypeMap.set(url, 'pdf');
            return;
        }

        // If extension is unknown, fetch content-type header
        try {
            const response = await fetch(url, { method: 'HEAD' });
            const contentType = response.headers.get('content-type') || '';

            if (contentType.startsWith('image/')) {
                this.fileTypeMap.set(url, 'image');
            } else if (contentType === 'application/pdf') {
                this.fileTypeMap.set(url, 'pdf');
            } else {
                this.fileTypeMap.set(url, 'unknown');
            }
        } catch {
            this.fileTypeMap.set(url, 'unknown');
        }
    }

    /**
     * Check if a URL is an image (used in template)
     */
    urlIsImage(url: string): boolean {
        return this.fileTypeMap.get(url) === 'image';
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
